import {selectArrayTernary} from '@coolcolduk/util';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {useNavigate} from 'react-router';
import {useMessage} from '@coolcolduk/ui-mui';
import type {QuizSessionWithScore, SessionQuestion} from '@cp/model';
import useRetryQuiz from '../../hooks/quizSession/useRetryQuiz';

dayjs.extend(relativeTime);

export interface SessionsListItemProps {
  session: QuizSessionWithScore & {isCompleted: boolean};
  onSessionClick: (session: QuizSessionWithScore & {isCompleted: boolean}) => void;
}

export default function SessionsListItem({session, onSessionClick}: SessionsListItemProps) {
  const navigate = useNavigate();
  const {retryQuiz, loading: retryLoading} = useRetryQuiz();
  const {showMessage} = useMessage();

  // Calculate duration for sessions
  const getDuration = () => {
    const start = dayjs(session.startedAt);
    const end = session.isCompleted && session.endedAt ? dayjs(session.endedAt) : dayjs();
    const duration = end.diff(start, 'minute');

    if (duration < 60) {
      return `${duration}m`;
    }
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  const duration = getDuration();
  const completePercentage = Math.round(
    (session.questions.filter((q: SessionQuestion) => q.selectedAnswer).length / session.questions.length) * 100,
  );

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const newSession = (await retryQuiz(session.moduleId, session.id)) as {id: string} | null | undefined;
      if (newSession?.id) {
        navigate(`/session/${newSession.id}`);
      }
    } catch {
      showMessage('Failed to retry session', 3000, 'error');
    }
  };

  return (
    <Box sx={{height: '100%'}}>
      <Card
        sx={{
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            boxShadow: 3,
          },
          transition: 'box-shadow 0.2s',
        }}
        onClick={() => onSessionClick(session)}
      >
        <CardContent sx={{display: 'flex', flexDirection: 'column', height: '100%', p: 2}}>
          <Stack spacing={2} sx={{height: '100%'}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <Typography variant="h6" component="div" sx={{fontSize: '1.1rem'}}>
                {session.moduleName}
              </Typography>
              <Chip
                label={session.isCompleted ? 'Completed' : 'Active'}
                color={session.isCompleted ? 'success' : 'primary'}
                size="small"
              />
            </Box>

            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {session.subject} • {session.level}
              </Typography>

              {session.examBoard && (
                <Typography variant="body2" color="text.secondary">
                  {session.examBoard}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary">
                {session.questions.length} questions
              </Typography>
            </Stack>

            <Divider />

            <Stack spacing={1} sx={{flexGrow: 1}}>
              <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                <Tooltip title={dayjs(session.startedAt).format('YYYY-MM-DD HH:mm:ss')} arrow>
                  <Typography variant="body2" color="text.secondary" sx={{cursor: 'help'}}>
                    Started {dayjs(session.startedAt).fromNow()}
                  </Typography>
                </Tooltip>
                <Typography variant="body2" color="primary" sx={{fontWeight: 500}}>
                  {duration}
                </Typography>
              </Box>

              {session.isCompleted && session.endedAt && (
                <Tooltip title={dayjs(session.endedAt).format('YYYY-MM-DD HH:mm:ss')} arrow>
                  <Typography variant="body2" color="text.secondary" sx={{cursor: 'help'}}>
                    Completed {dayjs(session.endedAt).fromNow()}
                  </Typography>
                </Tooltip>
              )}
            </Stack>

            <Box sx={{textAlign: 'center', mt: 'auto'}}>
              {session.isCompleted ? (
                <>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: selectArrayTernary(
                        [session.score >= 80, session.score >= 60],
                        ['success.main', 'warning.main'],
                        'error.main',
                      ),
                    }}
                  >
                    Score: {session.score}%
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={retryLoading ? <CircularProgress size={16} /> : <ReplayIcon />}
                    onClick={handleRetry}
                    disabled={retryLoading}
                    sx={{mt: 1, minWidth: 'auto'}}
                  >
                    Retry
                  </Button>
                </>
              ) : (
                <Typography variant="h6" color="primary" sx={{fontWeight: 600}}>
                  {Number.isNaN(completePercentage) ? 0 : completePercentage}% complete
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
